import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRef, useState } from "react";
import { useHistoryRowFocus, type HistoryFocusLease } from "./history-focus";
import { useDeleteConversation } from "@/hooks/useDeleteConversation";
import { RenameConversationDialog } from "./rename-conversation-dialog";
import { cn } from "@/utils/cn";
import { MoreVertical, PencilIcon, Trash2 } from "lucide-react";
import { useThemeMode } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { useStoredTheme } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";
import { usePlaygroundPresentation } from "./playground-presentation";
import {
  PlaygroundLink,
  usePlaygroundRouting,
} from "../routing/playground-routing";

export const PlaygroundListItem = ({
  title,
  id,
  active,
}: {
  title: string;
  id: string;
  active?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const { modern, mobile, setMobileHistoryOpen } = usePlaygroundPresentation();
  const isRTL = i18n.dir() === "rtl";
  const storedTheme = useStoredTheme();
  const { mode } = useThemeMode();
  const { id: currentRouteId, buildConversationDestination } =
    usePlaygroundRouting();

  const { mutateAsync } = useDeleteConversation(id, currentRouteId);
  const deletePendingRef = useRef(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const { owner, trigger, row } = useHistoryRowFocus(id);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuSession, setMenuSession] = useState<HistoryFocusLease | null>(null);
  const [dialogSession, setDialogSession] = useState<{ kind: "rename" | "delete"; lease: HistoryFocusLease } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = (kind: "rename" | "delete") => {
    setDeleteFailed(false);
    setDialogSession({ kind, lease: owner.claim(id, "dialog") });
    setDialogOpen(true);
    setMenuOpen(false);
  };
  return (
    <div
      ref={row}
      className={cn(
        modern ? "group flex min-h-[34px] min-w-0 items-center justify-between gap-1 rounded-lg border border-transparent px-2 py-1 text-foreground hover:bg-muted" : "flex items-center justify-between py-1 px-2 text-[var(--text-color)]  hover:bg-[var(--hover-bg)] cursor-pointer rounded-[8px]",
        modern ? { "border-border bg-card": active } : {
          "bg-[var(--active-bg)] font-semibold": active,
        },
      )}
      style={modern ? undefined :
        {
          "--text-color": storedTheme.sidebar.textColor[mode],
          "--active-bg":
            storedTheme.sidebar.activeLinkBackgroundColor[mode],
          "--hover-bg":
            storedTheme.sidebar.hoverBackgroundColor[mode],
        } as React.CSSProperties
      }
    >
      <PlaygroundLink
        className={modern ? "min-w-0 flex-1 rounded-sm focus-visible:outline-2 focus-visible:outline-ring" : "w-[90%]"}
        aria-current={active ? "page" : undefined}
        onClick={event => {
          if (modern && mobile && !event.defaultPrevented && event.button === 0 &&
            !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) setMobileHistoryOpen(false);
        }}
        to={buildConversationDestination(id)}
      >
        <span className={modern ? "block truncate text-[12.2px] font-normal leading-[18.24px]" : "truncate w-[90%] flex text-foreground text-[14px]"}>
          {title}
        </span>
      </PlaygroundLink>
      <DropdownMenu open={menuOpen} onOpenChange={(open) => {
        if (open) setMenuSession(owner.claim(id, "menu"));
        else if (!owner.isCurrent(menuSession)) return;
        setMenuOpen(open);
      }}>
        <DropdownMenuTrigger asChild>
          <Button ref={trigger} type="button" variant="ghost" size="icon" className={modern ? cn("size-[26px] shrink-0 rounded-full p-[5px] group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100", !active && !mobile && "opacity-0") : "size-4 shrink-0 rounded-sm p-0"} aria-label={t("playground.conversationActions", { name: title })}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent key={menuSession?.version ?? "idle"}
          className="bg-[var(--card-background)] border-[1px] border-solid border-ring"
          align={isRTL ? "end" : "start"}
          onFocusCapture={(event) => { owner.attach(menuSession, event.currentTarget); }}
          onCloseAutoFocus={(event) => { event.preventDefault(); owner.restore(menuSession); }}
        >
          <DropdownMenuItem onSelect={() => openDialog("rename")} className="cursor-pointer" dir={isRTL ? "rtl" : "ltr"}>
            <PencilIcon className="me-2 h-4 w-4" />
            <span>{t("common.rename")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openDialog("delete")} className="cursor-pointer text-destructive focus:text-destructive" dir={isRTL ? "rtl" : "ltr"}>
            <Trash2 className="me-2 h-4 w-4 text-destructive" />
            <span>{t("common.delete")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogSession?.kind === "rename" && (
        <RenameConversationDialog key={dialogSession.lease.version} conversationId={id} currentName={title} open={dialogOpen}
          onOpenChange={(open) => { if (owner.isCurrent(dialogSession.lease)) setDialogOpen(open); }}
          onOpenAutoFocus={(event) => { if (!owner.attach(dialogSession.lease, event.target)) event.preventDefault(); }}
          onCloseAutoFocus={(event) => { event.preventDefault(); owner.restore(dialogSession.lease); }}
        />
      )}
      {dialogSession?.kind === "delete" && (
        <AlertDialog key={dialogSession.lease.version} open={dialogOpen} onOpenChange={(open) => {
          if (!deletePendingRef.current && owner.isCurrent(dialogSession.lease)) setDialogOpen(open);
        }}>
          <AlertDialogContent className="bg-[var(--card-background)] border-[1px] border-solid border-ring"
            aria-busy={deletePending}
            onEscapeKeyDown={(event) => { if (deletePendingRef.current) event.preventDefault(); }}
            onOpenAutoFocus={(event) => { if (!owner.attach(dialogSession.lease, event.target)) event.preventDefault(); }}
            onCloseAutoFocus={(event) => { event.preventDefault(); owner.restore(dialogSession.lease); }}>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("playground.deleteConversationTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("playground.deleteConversationDescription")}</AlertDialogDescription>
            </AlertDialogHeader>
            {deleteFailed && <p role="alert" className="text-sm text-destructive dark:text-destructive-foreground">{t("playground.deleteConversationFailed")}</p>}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletePending} className="py-3 px-6 rounded-xs bg-transparent">{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction disabled={deletePending} className="py-3 px-6 rounded-xs text-destructive bg-transparent hover:bg-destructive/20" onClick={async (event) => {
                event.preventDefault();
                if (deletePendingRef.current || !owner.isCurrent(dialogSession.lease)) return;
                deletePendingRef.current = true;
                setDeletePending(true);
                setDeleteFailed(false);
                try {
                  await mutateAsync();
                  if (owner.isCurrent(dialogSession.lease)) setDialogOpen(false);
                } catch {
                  if (owner.isCurrent(dialogSession.lease)) setDeleteFailed(true);
                } finally {
                  deletePendingRef.current = false;
                  if (owner.isCurrent(dialogSession.lease)) setDeletePending(false);
                }
              }}>
                {deleteFailed ? t("common.retry") : t("common.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};
